import React, { useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { ArrowRight, MessageSquare, Calendar, Share2, Copy, Sparkles, TrendingDown, Zap, Shield } from 'lucide-react';

const THERAPY_DB = {
  // Weight Loss
  tirzepatide: { name: 'Tirzepatide', desc: 'Dual GIP/GLP-1 agonist with the strongest average weight loss efficacy in clinical trials.' },
  semaglutide: { name: 'Semaglutide', desc: 'Proven GLP-1 agonist with consistent weight loss results and broad availability.' },
  citradine: { name: 'Citradine', desc: 'Compounded weight loss support for enhanced metabolic function.' },
  satialean: { name: 'SatiaLean', desc: 'Appetite and satiety optimization compound.' },
  // Energy & Cellular Health
  nad: { name: 'NAD+', desc: 'Coenzyme that fuels mitochondrial energy production, combats brain fog and fatigue.' },
  motsc: { name: 'MOTS-c', desc: 'Mitochondrial peptide that improves insulin sensitivity and fat-as-fuel metabolism.' },
  glutathione: { name: 'Glutathione', desc: 'Master antioxidant that reduces oxidative stress and supports immune function.' },
  lipob: { name: 'Lipo-B', desc: 'B12, Methionine, Inositol, and Choline blend for liver and metabolic support.' },
  // Recovery
  bpc157: { name: 'BPC-157', desc: 'Targeted recovery support for specific areas of concern — joint, tendon, muscle, or soft tissue.' },
  wolverine: { name: 'Wolverine Stack', desc: 'Advanced recovery blend combining BPC-157 + TB-500 for broader, full-body recovery support.' },
  // Body Composition
  tesamorelin: { name: 'Tesamorelin', desc: 'Targets visceral belly fat specifically through natural growth hormone stimulation.' },
  sermorelin: { name: 'Sermorelin', desc: 'Growth hormone releasing peptide that restores deep sleep, muscle, and recovery.' },
  // Cognitive - no standalone product, use energy compounds
  // Sleep
  // Hormones
  hrt: { name: "Women's HRT", desc: 'Personalized hormone support for perimenopause and menopause symptoms.' },
  // Additional
  pt141: { name: 'PT-141', desc: 'Activates melanocortin receptors in the brain to restore desire and arousal.' },
  ortharex: { name: 'Ortharex', desc: 'Joint and musculoskeletal wellness support.' },
  minoxalune: { name: 'Minoxalune', desc: 'Vitality and wellness support compound.' },
};

const STACK_DB = {
  wolverine: { name: 'Wolverine Stack', desc: 'Advanced BPC-157 + TB-500 blend for comprehensive recovery and tissue support across multiple areas.' },
  mitochondrial: { name: 'Mitochondrial Stack', desc: 'NAD+ + MOTS-c + Glutathione for cellular energy and anti-aging.' },
  bodycomp: { name: 'Body Composition Stack', desc: 'GLP-1 + Tesamorelin + protein protocol for fat loss + muscle preservation.' },
  sleep: { name: 'Sleep Optimization Stack', desc: 'NAD+ + Sermorelin for deep sleep restoration and overnight recovery.' },
};

const BLOG_MAP = {
  'weight-loss': [
    { title: 'Why GLP-1 Works Differently for Different People', slug: 'why-glp1-works-differently-genetics' },
    { title: 'GLP-1 Plateau — Why It Happens and What Breaks It', slug: 'glp1-plateau-why-happens-how-to-break' },
  ],
  energy: [
    { title: 'Semaglutide and NAD+ Stack — What the Combination Does', slug: 'semaglutide-nad-stack' },
    { title: 'NAD+ and MOTS-c: The Mitochondrial Stack Explained', slug: 'nad-motsc-mitochondrial-stack' },
  ],
  recovery: [
    { title: 'The Wolverine Stack: BPC-157 and TB-500', slug: 'wolverine-stack-bpc157-tb500' },
    { title: 'GLP-1 + Peptides: The Stack Nobody Told You About', slug: 'glp1-peptides-stack' },
  ],
  sleep: [
    { title: 'Why Appetite Suppression Isn\'t Enough for Everyone', slug: 'why-appetite-suppression-not-enough' },
  ],
  cognitive: [
    { title: '7 Peptides People Over 50 Are Using to Feel Younger', slug: '7-peptides-over-50-feel-younger' },
  ],
  muscle: [
    { title: 'Tesamorelin vs Sermorelin: Differences & Stacking', slug: 'tesamorelin-vs-sermorelin' },
  ],
  hormones: [
    { title: 'Peptides for Women Over 40: The Complete Guide', slug: 'peptides-for-women-over-40' },
    { title: 'The Hormone and GLP-1 Connection for Women', slug: 'hormone-glp1-connection-women' },
  ],
  longevity: [
    { title: '7 Peptides People Over 50 Are Using to Feel Younger', slug: '7-peptides-over-50-feel-younger' },
    { title: 'Thymosin Alpha-1: The Immune Peptide', slug: 'thymosin-alpha-1-immune-peptide' },
  ],
};

function getRecommendation(answers) {
  const goals = answers.goals || [];
  const primary = goals[0];
  const challenge = answers.challenge;
  const energyLow = answers.energy === 'low' || answers.energy === 'very-low';
  const sleepPoor = answers.sleep === 'poor' || answers.sleep === 'very-poor';
  const triedBefore = challenge === 'plateau';
  const costConcern = challenge === 'cost';

  let rec = { primary: null, supporting: null, stack: null, insight: '', bryanNote: '' };

  switch (primary) {
    case 'weight-loss': {
      rec.primary = costConcern ? 'semaglutide' : 'tirzepatide';
      if (energyLow) rec.supporting = 'nad';
      else if (goals.includes('muscle') || goals.includes('recovery')) rec.supporting = 'sermorelin';
      else rec.supporting = 'lipob';
      if (goals.includes('muscle') && goals.includes('weight-loss')) rec.stack = 'bodycomp';
      rec.bryanNote = 'appetite and weight management';
      break;
    }
    case 'energy': {
      rec.primary = 'nad';
      if (goals.includes('longevity') || goals.includes('cognitive')) rec.supporting = 'motsc';
      else if (goals.includes('recovery')) rec.supporting = 'glutathione';
      else rec.supporting = 'motsc';
      if (goals.includes('cognitive') || goals.includes('longevity')) rec.stack = 'mitochondrial';
      rec.bryanNote = 'low energy and cellular health';
      break;
    }
    case 'recovery': {
      // Check challenge for hints about localized vs broad recovery
      const broadRecovery = challenge === 'body-comp' || challenge === 'fatigue' || goals.includes('muscle');
      if (broadRecovery) {
        rec.primary = 'wolverine';
        rec.supporting = 'nad';
      } else {
        rec.primary = 'bpc157';
        rec.supporting = 'wolverine';
      }
      rec.stack = 'wolverine';
      rec.bryanNote = 'recovery and tissue support';
      break;
    }
    case 'sleep': {
      rec.primary = 'sermorelin';
      rec.supporting = 'nad';
      rec.stack = 'sleep';
      rec.bryanNote = 'sleep quality and overnight recovery';
      break;
    }
    case 'cognitive': {
      rec.primary = 'nad';
      rec.supporting = 'motsc';
      rec.stack = 'mitochondrial';
      rec.bryanNote = 'cognitive performance and mental clarity';
      break;
    }
    case 'muscle': {
      rec.primary = 'tesamorelin';
      if (goals.includes('weight-loss')) rec.supporting = 'tirzepatide';
      else rec.supporting = 'sermorelin';
      rec.stack = 'bodycomp';
      rec.bryanNote = 'body composition and fat-to-muscle ratio';
      break;
    }
    case 'hormones': {
      rec.primary = 'hrt';
      rec.supporting = 'sermorelin';
      if (energyLow) rec.supporting = 'nad';
      rec.bryanNote = 'hormone optimization and overall wellness';
      break;
    }
    case 'longevity': {
      rec.primary = 'nad';
      rec.supporting = 'motsc';
      rec.stack = 'mitochondrial';
      rec.bryanNote = 'healthy aging and longevity';
      break;
    }
    default: {
      rec.primary = 'nad';
      rec.supporting = 'sermorelin';
      rec.bryanNote = 'overall health optimization';
    }
  }

  // Build insight
  const challengeMap = {
    appetite: 'appetite and cravings',
    fatigue: 'low energy and fatigue',
    sleep: 'sleep and recovery',
    'body-comp': 'body composition',
    focus: 'mental clarity and focus',
    hormones: 'hormone-related symptoms',
    plateau: 'previous approaches that stopped working',
    cost: 'finding an affordable approach',
  };
  const challengeText = challengeMap[challenge] || 'your health goals';
  const primaryName = THERAPY_DB[rec.primary]?.name || '';

  rec.insight = `Based on your answers, ${challengeText} was the strongest pattern in your responses. ${primaryName} is recommended as your primary therapy because it directly targets this area.`;
  if (rec.supporting) {
    rec.insight += ` Adding ${THERAPY_DB[rec.supporting]?.name} as support addresses the complementary systems that influence your results.`;
  }
  if (triedBefore) {
    rec.insight += " You've tried things before that didn't stick — this recommendation takes that into account. The combination approach addresses multiple systems simultaneously, which is often what makes the difference for people who've hit that wall.";
  }

  return rec;
}

function calcWeightProjection(answers) {
  const cw = Number(answers.currentWeight);
  const gw = Number(answers.goalWeight);
  if (!cw || !gw || cw <= gw) return null;

  const activityMultiplier = { sedentary: 0.85, light: 1.0, moderate: 1.1, 'very-active': 1.15 }[answers.activity] || 1.0;
  const monthlyRateLow = 0.015 * activityMultiplier;
  const monthlyRateHigh = 0.022 * activityMultiplier;

  let weightLow = cw, weightHigh = cw;
  for (let m = 0; m < 3; m++) {
    weightLow -= weightLow * monthlyRateLow;
    weightHigh -= weightHigh * monthlyRateHigh;
  }

  const lossLow = Math.round(cw - weightLow);
  const lossHigh = Math.round(cw - weightHigh);
  const projectedLow = Math.max(Math.round(weightLow), gw);
  const projectedHigh = Math.max(Math.round(weightHigh), gw);

  return { lossLow, lossHigh, projectedLow, projectedHigh };
}

const ForecastResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const answers = useMemo(() => {
    try {
      return JSON.parse(atob(searchParams.get('data') || ''));
    } catch { return null; }
  }, [searchParams]);

  const rec = useMemo(() => answers ? getRecommendation(answers) : null, [answers]);
  const weightProj = useMemo(() => answers?.goals?.includes('weight-loss') ? calcWeightProjection(answers) : null, [answers]);

  const blogLinks = useMemo(() => {
    if (!answers) return [];
    const links = [];
    (answers.goals || []).forEach(g => {
      (BLOG_MAP[g] || []).forEach(b => {
        if (!links.find(l => l.slug === b.slug)) links.push(b);
      });
    });
    return links.slice(0, 3);
  }, [answers]);

  if (!answers || !rec) {
    return (
      <div className="min-h-screen bg-[#020817] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400 mb-4">No forecast data found.</p>
          <Button onClick={() => navigate('/forecast')} className="rounded-full">Start Your Forecast</Button>
        </div>
      </div>
    );
  }

  const isWeightLoss = answers.goals?.includes('weight-loss');
  const shareText = "Hey — I just used this AI tool that builds a personalized health optimization plan based on your goals. It's not a generic quiz — it actually gives you a real recommendation. Takes 3 minutes: myweightforecast.com";

  const handleCopyLink = () => {
    navigator.clipboard?.writeText('https://myweightforecast.com/forecast');
  };

  const handleShare = (platform) => {
    const url = 'https://myweightforecast.com/forecast';
    const encoded = encodeURIComponent(shareText);
    const links = {
      sms: `sms:?body=${encoded}`,
      whatsapp: `https://wa.me/?text=${encoded}`,
      email: `mailto:?subject=Check this out&body=${encoded}`,
    };
    window.open(links[platform], '_blank');
  };

  return (
    <div className="min-h-screen bg-[#020817] text-white">
      <Helmet>
        <title>Your AI Forecast Results | MyWeightForecast</title>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 pt-12 pb-24">

        {/* Section 1 — Forecast Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-6">
            <Sparkles className="h-4 w-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">Your Personalized Forecast</span>
          </div>

          {isWeightLoss && weightProj ? (
            <>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-6">Your 90-Day Weight Loss Forecast</h1>
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="bg-slate-950/60 border border-white/10 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">Current</p>
                  <p className="text-2xl font-bold">{answers.currentWeight}<span className="text-sm text-slate-400"> lbs</span></p>
                </div>
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
                  <p className="text-xs text-cyan-400 mb-1">Est. Loss</p>
                  <p className="text-2xl font-bold text-cyan-400">{weightProj.lossLow}-{weightProj.lossHigh}<span className="text-sm"> lbs</span></p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                  <p className="text-xs text-emerald-400 mb-1">Projected</p>
                  <p className="text-2xl font-bold text-emerald-400">{weightProj.projectedHigh}-{weightProj.projectedLow}<span className="text-sm"> lbs</span></p>
                </div>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Your 90-Day Optimization Forecast</h1>
              <p className="text-slate-400 text-lg max-w-xl mx-auto">
                Based on your goals — {answers.goals?.map(g => g.replace('-', ' ')).join(', ')} — here's your personalized optimization plan.
              </p>
            </>
          )}
        </motion.div>

        {/* Section 2 — Bryan Would Probably Start Here + Recommendation Cards */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-12">
          <h2 className="font-heading text-xl font-bold mb-2 text-center">Based on your answers, Bryan would probably start here:</h2>
          {rec.bryanNote && (
            <p className="text-center text-slate-500 text-sm mb-6">
              <em>{rec.bryanNote}</em> was the strongest pattern in your responses.
            </p>
          )}

          <div className="space-y-4">
            {rec.primary && THERAPY_DB[rec.primary] && (
              <div className="bg-gradient-to-br from-cyan-500/10 to-emerald-500/5 border border-cyan-500/20 rounded-2xl p-6" data-testid="rec-primary">
                <div className="flex items-start gap-3 mb-2">
                  <TrendingDown className="h-5 w-5 text-cyan-400 mt-1 shrink-0" />
                  <div>
                    <span className="text-xs text-cyan-400 font-medium uppercase tracking-wider">Your Primary Recommendation</span>
                    <h3 className="font-heading text-xl font-bold text-white mt-1">{THERAPY_DB[rec.primary].name}</h3>
                    <p className="text-slate-400 mt-1">{THERAPY_DB[rec.primary].desc}</p>
                  </div>
                </div>
              </div>
            )}

            {rec.supporting && THERAPY_DB[rec.supporting] && (
              <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-6" data-testid="rec-supporting">
                <div className="flex items-start gap-3 mb-2">
                  <Zap className="h-5 w-5 text-emerald-400 mt-1 shrink-0" />
                  <div>
                    <span className="text-xs text-emerald-400 font-medium uppercase tracking-wider">Recommended Support</span>
                    <h3 className="font-heading text-xl font-bold text-white mt-1">{THERAPY_DB[rec.supporting].name}</h3>
                    <p className="text-slate-400 mt-1">{THERAPY_DB[rec.supporting].desc}</p>
                  </div>
                </div>
              </div>
            )}

            {rec.stack && STACK_DB[rec.stack] && (
              <div className="bg-slate-950/60 border border-purple-500/10 rounded-2xl p-6" data-testid="rec-stack">
                <div className="flex items-start gap-3 mb-2">
                  <Shield className="h-5 w-5 text-purple-400 mt-1 shrink-0" />
                  <div>
                    <span className="text-xs text-purple-400 font-medium uppercase tracking-wider">Optional Stack Upgrade</span>
                    <h3 className="font-heading text-xl font-bold text-white mt-1">{STACK_DB[rec.stack].name}</h3>
                    <p className="text-slate-400 mt-1">{STACK_DB[rec.stack].desc}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Section 3 — Personalized Insight */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-12">
          <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-6">
            <p className="text-slate-300 leading-relaxed" data-testid="personalized-insight">{rec.insight}</p>
          </div>
        </motion.div>

        {/* Section 4 — Conversion CTAs */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mb-12">
          <div className="text-center bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border border-emerald-500/20 rounded-2xl p-8">
            <h2 className="font-heading text-xl sm:text-2xl font-bold mb-3">
              Want me to walk you through your exact plan?
            </h2>
            <p className="text-slate-400 mb-6">We can map it out and get you started this week.</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
              <a
                href="sms:8016518363"
                className="inline-flex items-center justify-center bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-8 py-4 text-lg font-bold shadow-lg transition-all"
                data-testid="cta-text"
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Text Me My Results
              </a>
              <a
                href="https://calendly.com/bryanm8623/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center border-2 border-cyan-500/30 hover:border-cyan-500/50 bg-cyan-500/5 text-cyan-300 rounded-full px-8 py-4 text-lg font-bold transition-all"
                data-testid="cta-calendly"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Let's Map Out Your Plan
              </a>
            </div>
            <p className="text-sm text-slate-500">No commitment required. Most people just want to ask a few questions first — that's completely fine.</p>
          </div>
        </motion.div>

        {/* Section 5 — Blog Links */}
        {blogLinks.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="mb-12">
            <h3 className="font-heading text-lg font-bold mb-4">Keep learning at your own pace</h3>
            <p className="text-slate-500 text-sm mb-4">Based on your answers, these might help:</p>
            <div className="space-y-3">
              {blogLinks.map(b => (
                <Link
                  key={b.slug}
                  to={`/blog/${b.slug}`}
                  className="flex items-center justify-between p-4 rounded-xl border border-white/10 bg-slate-950/40 hover:border-cyan-500/30 transition-all group"
                >
                  <span className="text-slate-300 group-hover:text-white transition-colors">{b.title}</span>
                  <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-cyan-400 transition-colors shrink-0 ml-2" />
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* Section 6 — Share */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="text-center">
          <p className="text-slate-500 text-sm mb-4">Know someone who might want to see their optimization forecast?</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <button onClick={handleCopyLink} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-white/20 text-sm transition-all" data-testid="share-copy">
              <Copy className="h-4 w-4" /> Copy Link
            </button>
            <button onClick={() => handleShare('sms')} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-white/20 text-sm transition-all" data-testid="share-sms">
              <MessageSquare className="h-4 w-4" /> SMS
            </button>
            <button onClick={() => handleShare('whatsapp')} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-white/20 text-sm transition-all" data-testid="share-whatsapp">
              <Share2 className="h-4 w-4" /> WhatsApp
            </button>
            <button onClick={() => handleShare('email')} className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-white/20 text-sm transition-all" data-testid="share-email">
              <Share2 className="h-4 w-4" /> Email
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ForecastResults;
